import React, { useId } from "react";
import PropTypes from "prop-types";
import { Box, Button, Typography } from "@mui/material";
import { EditOutlined } from "@mui/icons-material";
import { alpha } from "@mui/material/styles";
import { motion } from "framer-motion";
import { colors } from "../../theme/colors";
import TooltipComponent from "../common/Tooltip";
import StatusDot from "../common/StatusDot";
import {
    isOuroVerifiedAccount,
    OuroVerifiedBadge,
    ProfileRankBadge,
} from "./profileIdentityBadges";

const MotionBox = motion(Box);

const BANNER_SX = {
    position: "relative",
    overflow: "hidden",
    display: "flex",
    alignItems: "flex-end",
    width: "100%",
    minWidth: 0,
    minHeight: { xs: "7.5rem", md: "9.375rem" },
    mb: { xs: "2.25rem", md: "3rem" },
    px: { xs: "1.1rem", md: "1.85rem" },
    py: { xs: "1.1rem", md: "1.35rem" },
    borderRadius: 0,
    border: "none",
    borderBottom: "1px solid rgba(255,255,255,0.05)",
    boxSizing: "border-box",
    background: `
        linear-gradient(
            180deg,
            rgba(10,10,11,0.2) 0%,
            rgba(10,10,11,0.92) 100%
        ),
        radial-gradient(
            ellipse 900px 500px at 30% -20%,
            rgba(201,168,76,0.16),
            transparent 60%
        ),
        linear-gradient(
            135deg,
            #1c1710 0%,
            #0d0d0f 55%,
            #0a0a0b 100%
        )
    `,
    boxShadow: `
        inset 0 1px 0 rgba(255,255,255,0.04)
    `,
    "&::after": {
        content: '""',
        position: "absolute",
        top: 0,
        left: "8%",
        right: "8%",
        height: "1px",
        background: `
            linear-gradient(
                90deg,
                transparent,
                rgba(255,255,255,0.14),
                transparent
            )
        `,
        pointerEvents: "none",
    },
};

const CUSTOMIZE_BUTTON_SX = {
    position: "absolute",
    top: { xs: "0.7rem", md: "0.85rem" },
    right: { xs: "0.75rem", md: "1rem" },
    zIndex: 2,
    minHeight: 0,
    borderRadius: "6px",
    textTransform: "none",
    fontSize: "0.68rem",
    fontWeight: 600,
    letterSpacing: "0.06em",
    color: alpha("#fff", 0.48),
    border: "1px solid rgba(255,255,255,0.12)",
    background: `
        linear-gradient(
            180deg,
            rgba(255,255,255,0.04) 0%,
            rgba(0,0,0,0.28) 100%
        )
    `,
    boxShadow: "inset 0 1px 0 rgba(255,255,255,0.03)",
    backdropFilter: "blur(10px)",
    transition:
        "color 0.15s ease-in-out, border-color 0.15s ease-in-out, background-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out",
    "&:hover": {
        color: alpha("#F6D28B", 0.92),
        borderColor: "rgba(246,199,107,0.34)",
        background: `
            linear-gradient(
                180deg,
                rgba(40,30,16,0.72) 0%,
                rgba(18,14,8,0.82) 100%
            )
        `,
        boxShadow: `
            inset 0 1px 0 rgba(255,255,255,0.05),
            0 0 16px rgba(232,163,61,0.06)
        `,
        "& .MuiButton-startIcon": {
            color: alpha("#F6D28B", 0.92),
        },
    },
};

const ACTIVE_PROFILE_STATUSES = new Set([
    "inLobby",
    "inPrivateLobby",
    "inYourLobby",
    "inMatch",
    "inMatchMaking",
]);

function getProfileStatusLabel(playerStatus) {
    switch (playerStatus) {
        case "inMatch":
            return "In Match";
        case "inMatchMaking":
            return "In Matchmaking";
        case "inPrivateLobby":
        case "inYourLobby":
            return "In a Lobby";
        case "inLobby":
            return "In Lobby";
        default:
            return "Offline";
    }
}

export default function ProfileBanner({
    profileData,
    globalRank = null,
    playerStatus = "offline",
    isOwnProfile = false,
    isLoading = false,
    onCustomizeBanner,
}) {
    const gradientId = "profile-banner-verified-" + useId().replace(/:/g, "");
    const nickname = profileData?.nickname || "";
    const isVerified = isOuroVerifiedAccount(profileData);
    const statusLabel = getProfileStatusLabel(playerStatus);
    const isStatusActive = ACTIVE_PROFILE_STATUSES.has(playerStatus);
    const statusColor = isStatusActive
        ? colors.state.success.main
        : colors.text.muted;

    return (
        <MotionBox
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
            sx={{
                ...BANNER_SX,
                "&::before": {
                    content: '""',
                    position: "absolute",
                    width: { xs: "72%", md: "39%" },
                    height: "170%",
                    right: "-8%",
                    bottom: "-62%",
                    background:
                        "radial-gradient(ellipse at 88% 54%, " +
                        alpha(statusColor, isStatusActive ? 0.17 : 0.075) +
                        " 0%, " +
                        alpha(statusColor, isStatusActive ? 0.055 : 0.026) +
                        " 36%, transparent 68%)",
                    filter: "blur(10px)",
                    opacity: isOwnProfile
                        ? 0
                        : isStatusActive
                          ? { xs: 0, sm: 0.96 }
                          : { xs: 0, sm: 0.68 },
                    pointerEvents: "none",
                    transition: "background 320ms ease, opacity 320ms ease",
                    zIndex: 0,
                },
            }}
        >
            {isOwnProfile ? (
                <TooltipComponent title="Coming soon..." placement="bottom">
                    <Button
                        type="button"
                        onClick={onCustomizeBanner}
                        aria-label="Customize banner"
                        startIcon={
                            <EditOutlined sx={{ fontSize: "0.9rem !important" }} />
                        }
                        sx={{
                            ...CUSTOMIZE_BUTTON_SX,
                            minWidth: { xs: "2.1rem", md: 0 },
                            width: { xs: "2.1rem", md: "auto" },
                            height: { xs: "2.1rem", md: "auto" },
                            px: { xs: 0, md: "0.75rem" },
                            py: { xs: 0, md: "0.4rem" },
                            justifyContent: "center",
                            "& .MuiButton-startIcon": {
                                margin: { xs: 0, md: "0 0.4rem 0 0" },
                                color: alpha("#fff", 0.45),
                                transition: "color 0.15s ease-in-out",
                            },
                            "& .MuiButton-startIcon > *:nth-of-type(1)": {
                                fontSize: "0.9rem",
                            },
                        }}
                    >
                        <Box
                            component="span"
                            sx={{ display: { xs: "none", md: "inline" } }}
                        >
                            Customize banner
                        </Box>
                    </Button>
                </TooltipComponent>
            ) : null}

            <Box
                sx={{
                    position: "relative",
                    zIndex: 1,
                    display: "flex",
                    flexDirection: { xs: "column", sm: "row" },
                    alignItems: { xs: "stretch", sm: "center" },
                    justifyContent: "space-between",
                    gap: { xs: "0.9rem", sm: "1.25rem", md: "2rem" },
                    width: "100%",
                    minWidth: 0,
                }}
            >
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        flexWrap: "wrap",
                        gap: { xs: "0.55rem", md: "0.85rem" },
                        minWidth: 0,
                        flex: "1 1 auto",
                        maxWidth: "100%",
                        pr: isOwnProfile
                            ? { xs: "3rem", sm: 0, md: 0 }
                            : 0,
                    }}
                >
                    {isLoading ? (
                        <Box
                            sx={{
                                width: { xs: "10rem", md: "14rem" },
                                height: { xs: "2rem", md: "2.6rem" },
                                borderRadius: "6px",
                                backgroundColor: "rgba(255,255,255,0.06)",
                            }}
                        />
                    ) : (
                        <>
                            <Typography
                                component="h1"
                                title={nickname}
                                sx={{
                                    m: 0,
                                    minWidth: 0,
                                    maxWidth: "100%",
                                    color: colors.text.primary,
                                    fontFamily: "'Orbitron', sans-serif",
                                    fontSize: {
                                        xs: "1.65rem",
                                        sm: "2.1rem",
                                        md: "2.55rem",
                                    },
                                    fontWeight: 700,
                                    letterSpacing: "0.01em",
                                    lineHeight: 1,
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap",
                                    textShadow: "0 2px 12px rgba(0,0,0,0.6)",
                                }}
                            >
                                {nickname}
                            </Typography>
                            {isVerified ? (
                                <OuroVerifiedBadge
                                    gradientId={gradientId}
                                    size="hero"
                                />
                            ) : null}
                            <ProfileRankBadge rank={globalRank} />
                        </>
                    )}
                </Box>
                {isOwnProfile ? null : isLoading ? (
                    <Box
                        sx={{
                            display: { xs: "none", sm: "block" },
                            width: "9.5rem",
                            height: "3rem",
                            flexShrink: 0,
                            alignSelf: { xs: "flex-end", sm: "center" },
                            borderRadius: "10px",
                            backgroundColor: "rgba(255,255,255,0.045)",
                        }}
                    />
                ) : (
                    <MotionBox
                        role="status"
                        aria-label={"Player status: " + statusLabel}
                        initial={{ opacity: 0, x: 12 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{
                            duration: 0.45,
                            delay: 0.14,
                            ease: [0.22, 1, 0.36, 1],
                        }}
                        sx={{
                            position: "relative",
                            display: { xs: "none", sm: "flex" },
                            alignItems: "center",
                            gap: "0.65rem",
                            minWidth: isStatusActive ? { sm: "8.25rem" } : "auto",
                            px: isStatusActive ? "0.25rem" : "0.72rem",
                            py: isStatusActive ? "0.42rem" : "0.5rem",
                            flexShrink: 0,
                            alignSelf: { xs: "flex-end", sm: "center" },
                            overflow: "visible",
                            borderRadius: isStatusActive ? 0 : "999px",
                            border: isStatusActive
                                ? "none"
                                : "1px solid rgba(255,255,255,0.065)",
                            background: isStatusActive
                                ? "transparent"
                                : "linear-gradient(100deg, rgba(255,255,255,0.032), rgba(255,255,255,0.014))",
                            boxShadow: isStatusActive
                                ? "none"
                                : "inset 0 1px 0 rgba(255,255,255,0.025), 0 8px 24px rgba(0,0,0,0.12)",
                        }}
                    >
                        {isStatusActive ? (
                            <StatusDot
                                online
                                variant="inline"
                                sx={{
                                    fontSize: "0.72rem",
                                    color: statusColor,
                                }}
                            />
                        ) : null}

                        <Typography
                            sx={{
                                fontSize: { xs: "0.72rem", md: "0.78rem" },
                                fontWeight: 700,
                                lineHeight: 1,
                                letterSpacing: "0.09em",
                                textTransform: "uppercase",
                                color: isStatusActive
                                    ? "#76F3A6"
                                    : alpha(colors.text.primary, 0.56),
                                whiteSpace: "nowrap",
                                textShadow: isStatusActive
                                    ? "0 0 18px " + alpha(statusColor, 0.18)
                                    : "none",
                            }}
                        >
                            {statusLabel}
                        </Typography>
                    </MotionBox>
                )}
            </Box>
        </MotionBox>
    );
}

ProfileBanner.propTypes = {
    globalRank: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    profileData: PropTypes.object,
    playerStatus: PropTypes.string,
    isOwnProfile: PropTypes.bool,
    isLoading: PropTypes.bool,
    onCustomizeBanner: PropTypes.func,
};
